import { useEffect, useRef, useState } from "react";

const Briefcase = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const Wrench = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);
const FileText = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const Calculator = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="8" y2="10" />
    <line x1="12" y1="10" x2="12" y2="10" />
    <line x1="16" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="8" y2="14" />
    <line x1="12" y1="14" x2="12" y2="14" />
    <line x1="16" y1="14" x2="16" y2="14" />
    <line x1="8" y1="18" x2="16" y2="18" />
  </svg>
);
const ShieldIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const ScaleIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 3 21 3 21 8" />
    <line x1="4" y1="20" x2="21" y2="3" />
    <polyline points="21 16 21 21 16 21" />
    <line x1="15" y1="15" x2="21" y2="21" />
    <line x1="4" y1="4" x2="9" y2="9" />
  </svg>
);
const CheckIcon = ({ size = 11, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ArrowRight = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const steps = [
  {
    number: "01",
    title: "Start",
    subtitle: "Register Your Business",
    description:
      "Turn your vision into reality. Choose the right structure and register your business or non-profit with confidence.",
    color: "hsl(25, 95%, 53%)",
    icon: Briefcase,
    cta: "Start Your Dream",
    sections: [
      {
        heading: "Register Your Business",
        items: [
          "Sole Proprietorship",
          "Partnership Firm",
          "Limited Liability Partnership (LLP)",
          "Private Limited Company",
          "One Person Company (OPC)",
          "Public Limited Company",
        ],
      },
      {
        heading: "Register Your Non-Profit",
        items: [
          "Trust Registration",
          "Society Registration",
          "Section 8 Company",
        ],
      },
    ],
  },
  {
    number: "02",
    title: "Build",
    subtitle: "Run & License Your Business",
    description:
      "Get everything you need to operate legally — from business support to all required licenses and registrations.",
    color: "hsl(174, 72%, 40%)",
    icon: Wrench,
    cta: "Build It Right",
    sections: [
      {
        heading: "Run Your Business",
        items: [
          "Get a Business Attorney",
          "Finances and Funding",
          "Branding and Marketing",
          "Operations and Management",
        ],
      },
      {
        heading: "Get Business Licenses",
        items: [
          "GST Registration",
          "Udyam Registration",
          "FSSAI Registration",
          "Trade License",
          "Shop & Establishment License",
          "Drug License",
          "IEC Import Export Code",
        ],
      },
    ],
  },
  {
    number: "03",
    title: "Comply",
    subtitle: "Tax & Compliance",
    description:
      "Stay on top of every tax obligation — direct, indirect, and return filing — without the stress.",
    color: "hsl(221, 83%, 53%)",
    icon: FileText,
    cta: "Stay Compliant",
    sections: [
      {
        heading: "Direct Tax",
        items: [
          "Income Tax Return",
          "Corporate Tax",
          "Capital Gains Tax",
          "Securities Transaction Tax",
        ],
      },
      {
        heading: "Indirect Tax",
        items: [
          "GST Registration",
          "GST Return Filing",
          "GST Notice Reply",
          "GST Audit",
        ],
      },
      {
        heading: "Filing Tax Returns",
        items: ["Income Tax", "GST", "TDS / e-TDS", "EPFO", "ESIC"],
      },
    ],
  },
  {
    number: "04",
    title: "Control",
    subtitle: "Accounting & Reporting",
    description:
      "Take full control of your finances with professional accounting, reporting, and audit services.",
    color: "hsl(142, 71%, 45%)",
    icon: Calculator,
    cta: "Take Control",
    sections: [
      {
        heading: "Accounting & Reporting",
        items: [
          "Accounting & Bookkeeping",
          "MIS Reporting",
          "Financial Statements",
        ],
      },
      {
        heading: "Audit Services",
        items: ["Internal Audit", "Statutory Audit", "Tax Audit"],
      },
    ],
  },
  {
    number: "05",
    title: "Protect",
    subtitle: "Protect Your Business",
    description:
      "Safeguard your brand, intellectual property, and business identity from day one.",
    color: "hsl(262, 83%, 58%)",
    icon: ShieldIcon,
    cta: "Protect Now",
    sections: [
      {
        heading: "Trademark & IP",
        items: [
          "Trademark Registration",
          "Trademark Search",
          "Trademark Monitoring",
          "Copyright Registration",
          "Patent Filing",
        ],
      },
    ],
  },
  {
    number: "06",
    title: "Defend",
    subtitle: "Legal Services",
    description:
      "Navigate legal challenges confidently with expert drafting, documentation, and dispute resolution support.",
    color: "hsl(0, 75%, 50%)",
    icon: ScaleIcon,
    cta: "Get Legal Help",
    sections: [
      {
        heading: "Legal Services",
        items: [
          "Legal Notice Drafting",
          "Reply to Legal Notice",
          "Agreements & Contracts",
          "Affidavit & Documentation",
        ],
      },
    ],
  },
];

function JourneyCard({ step, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const Icon = step.icon;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.05 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        gap: "2rem",
        paddingBottom: index === steps.length - 1 ? 0 : "2rem",
      }}
    >
      {/* Icon marker */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: "3.5rem",
            height: "3.5rem",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            boxShadow: `0 4px 20px ${step.color}55`,
            backgroundColor: step.color,
            transform: visible ? "scale(1)" : "scale(0)",
            opacity: visible ? 1 : 0,
            transition: "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
            transitionDelay: "100ms",
            flexShrink: 0,
          }}
        >
          <Icon size={22} />
        </div>
      </div>

      {/* Card */}
      <div
        style={{
          flex: 1,
          borderRadius: "1.25rem",
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
          boxShadow: "0 4px 28px rgba(0,0,0,0.08)",
          padding: "1.75rem 2rem",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(32px)",
          transition: "opacity 0.6s ease, transform 0.6s ease",
          transitionDelay: `${80 * index}ms`,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: "0.75rem",
          }}
        >
          <span
            style={{
              fontSize: "2.8rem",
              fontWeight: 900,
              lineHeight: 1,
              color: step.color,
            }}
          >
            {step.number}
          </span>
          <div>
            <h3
              style={{
                fontSize: "1.35rem",
                fontWeight: 700,
                margin: 0,
                color: "#111827",
              }}
            >
              {step.title}
            </h3>
            <p
              style={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color: "#6b7280",
                margin: 0,
              }}
            >
              {step.subtitle}
            </p>
          </div>
        </div>

        <p
          style={{
            color: "#6b7280",
            fontSize: "0.88rem",
            lineHeight: 1.65,
            marginBottom: "1.5rem",
            marginTop: 0,
          }}
        >
          {step.description}
        </p>

        {/* Sections */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.25rem",
            marginBottom: "1.5rem",
          }}
        >
          {step.sections.map((section) => (
            <div key={section.heading}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "0.6rem",
                }}
              >
                <div
                  style={{
                    width: "3px",
                    height: "1rem",
                    borderRadius: "2px",
                    backgroundColor: step.color,
                  }}
                />
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: step.color,
                  }}
                >
                  {section.heading}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.4rem",
                  paddingLeft: "0.5rem",
                }}
              >
                {section.items.map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                    }}
                  >
                    <div
                      style={{
                        width: "1.1rem",
                        height: "1.1rem",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        backgroundColor: step.color + "20",
                      }}
                    >
                      <CheckIcon size={10} style={{ color: step.color }} />
                    </div>
                    <span style={{ fontSize: "0.83rem", color: "#374151" }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button
          style={{
            backgroundColor: step.color,
            color: "white",
            border: "none",
            borderRadius: "9999px",
            padding: "0.6rem 1.4rem",
            fontSize: "0.875rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "0.85")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
        >
          {step.cta} <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

export default function JourneyTimeline() {
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      {/* Hero */}
      <section
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "6rem 1rem",
        }}
      >
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#9ca3af",
            marginBottom: "1rem",
          }}
        >
          Your Complete Business Partner
        </span>
        <h1
          style={{
            fontSize: "clamp(2rem, 6vw, 3.5rem)",
            fontWeight: 900,
            color: "#111827",
            margin: "0 0 1rem",
            lineHeight: 1.1,
            maxWidth: "48rem",
          }}
        >
          The Business Journey
        </h1>
        <p
          style={{
            fontSize: "1.1rem",
            color: "#6b7280",
            maxWidth: "36rem",
            margin: "0 0 2rem",
            lineHeight: 1.65,
          }}
        >
          From idea to empire — we walk with you at every stage of your
          entrepreneurial journey.
        </p>
        <button
          style={{
            backgroundColor: "#111827",
            color: "white",
            border: "none",
            borderRadius: "9999px",
            padding: "0.85rem 2rem",
            fontSize: "1rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = "#374151")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = "#111827")
          }
          onClick={() => {
            // redirect user to the Google Calendar booking page
            window.location.href =
              "https://calendar.app.google/mvZ1WhAt3Hn7BJ9z6";
          }}
        >
          Claim Free Consultation <ArrowRight size={18} />
        </button>
      </section>

      {/* Timeline */}
      <section
        style={{
          maxWidth: "48rem",
          margin: "0 auto",
          padding: "0 1.5rem 6rem",
          position: "relative",
        }}
      >
        {/* Static vertical line */}
        <div
          style={{
            position: "absolute",
            left: "calc(1.75rem + 1.5rem)",
            top: 0,
            bottom: 0,
            width: "3px",
            borderRadius: "9999px",
            background:
              "linear-gradient(to bottom, hsl(25,95%,53%), hsl(174,72%,40%), hsl(221,83%,53%), hsl(142,71%,45%), hsl(262,83%,58%), hsl(0,75%,50%))",
          }}
        />

        {steps.map((step, i) => (
          <JourneyCard key={step.number} step={step} index={i} />
        ))}
      </section>
    </div>
  );
}
